import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { ProductCollectionOverlayTemplate } from '@/components/signup/layouts/product-overlay-template';
import { Dropdown } from '@/components/signup/site-activation/components/common/dropdown';
import { useSiteActivationContext } from '@/components/signup/site-activation/hooks/site-activation-context';
import { SiteActivation } from '@/components/signup/site-activation/site-activation';
import { isProduction } from '@/components/signup/utils/environment';
import { useProductCollectionUpliftableSites } from '@/components/user-context/helpers/cohorts';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	ExistingSite,
	GetSignupAccountOrgDetailsResponse,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { Button } from '@/libraries/design/components/button/src/button';

import { Headings } from '../components/headings';

import { ProductCollectionSwitchYourSiteMessage } from './product-collection-switch-your-site-message';

/**
 * The user is an admin of a site with a standalone JPD on the edition they are signing up for.
 * They can switch that site to Product Collection in Atlassian Administration (STARB-4846).
 */
export const ProductCollectionUpliftEligibleUser = () => (
	<ProductCollectionOverlayTemplate size="sm">
		<Headings
			main={
				<FormattedMessage
					id="signup.productCollection.uplift.heading"
					defaultMessage="Switch your site to Product Collection"
				/>
			}
		/>
		<SiteActivation UpliftEligibleUser={<ProductCollectionUpliftPicker />} />
	</ProductCollectionOverlayTemplate>
);

const ProductCollectionUpliftPicker = () => {
	const [loading, setLoading] = useState(true);
	const [orgs, setOrgs] = useState<GetSignupAccountOrgDetailsResponse[]>([]);
	const [upliftableSites, setUpliftableSites] = useState<ExistingSite[]>([]);
	const { sitePickerValue, setSitePickerValue } = useSiteActivationContext();
	const potentiallyUpliftableSites = useProductCollectionUpliftableSites();
	const stargateClient = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Only sites in orgs the user administers can be switched in Atlassian Administration.
	// One failing org lookup only drops that org's sites; if the org list fails, the user sees
	// the "add it from Atlassian Administration" message.
	useEffect(() => {
		if (!potentiallyUpliftableSites) {
			return;
		}
		stargateClient
			.getAccountOrgs({})
			.then(async ({ accountOrgs }) => {
				if (!accountOrgs) {
					return;
				}
				const orgIds = accountOrgs.filter(({ isAdmin }) => isAdmin).map(({ id }) => id);
				const orgDetailsResults = await Promise.allSettled(
					orgIds.map((orgId) => stargateClient.getSignupAccountOrgDetails({ orgId })),
				);
				const orgDetails = orgDetailsResults.flatMap((result) =>
					result.status === 'fulfilled' ? [result.value] : [],
				);
				const eligibleCloudIds = orgDetails.flatMap((org) => org.organization?.siteIds ?? []);
				setOrgs(orgDetails);
				setUpliftableSites(
					potentiallyUpliftableSites.filter((site) => eligibleCloudIds.includes(site.cloudId)),
				);
			})
			.catch(() => {
				setUpliftableSites([]);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [stargateClient, potentiallyUpliftableSites]);

	// The site activation context defaults to cross-flow sites, which never include JPD sites.
	useEffect(() => {
		if (
			upliftableSites.length > 0 &&
			!upliftableSites.some(({ cloudId }) => cloudId === sitePickerValue?.cloudId)
		) {
			setSitePickerValue(upliftableSites[0]);
		}
	}, [setSitePickerValue, sitePickerValue?.cloudId, upliftableSites]);

	useEffect(() => {
		if (loading) {
			return;
		}
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'viewed',
			actionSubject: 'productCollectionUpliftScreen',
			attributes: {
				eligibleSitesCount: upliftableSites.length,
			},
		}).fire();
	}, [loading, createAnalyticsEvent, upliftableSites.length]);

	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!sitePickerValue) {
			return;
		}
		const orgId = orgs.find((org) => org.organization?.siteIds?.includes(sitePickerValue.cloudId))
			?.organization?.id;

		createAnalyticsEvent({
			eventType: 'ui',
			action: 'submitted',
			actionSubject: 'productCollectionUpliftForm',
		}).fire();

		const adminHubUrl = isProduction()
			? 'https://admin.atlassian.com'
			: 'https://admin.stg.atlassian.com';
		// TODO(STARB-4846): confirm the Product Collection add-app drawer with Admin Experience.
		window.location.assign(
			`${adminHubUrl}/o/${orgId}/atlassian-apps?openAddAppDrawer=ProductCollection`,
		);
	};

	if (loading) {
		return (
			<div css={spinnerContainerCss}>
				<Spinner size="large" />
			</div>
		);
	}

	if (!upliftableSites.length) {
		return <ProductCollectionSwitchYourSiteMessage />;
	}

	return (
		<form css={formCss} onSubmit={onSubmit}>
			<Dropdown sites={upliftableSites} />
			<Button css={buttonCss} type="submit">
				<FormattedMessage id="WacSignupForm.upgradeButtonText" defaultMessage="Upgrade now" />
			</Button>
		</form>
	);
};

const formCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300', '24px'),
	width: '100%',
});

const buttonCss = css({
	maxWidth: 'none',
});

const spinnerContainerCss = css({
	alignItems: 'center',
	display: 'flex',
	flexGrow: 1,
	justifyContent: 'center',
	padding: 40,
});
